// src/pages/Forgot.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { resolveTenantHint } from '../utils/tenant';

export default function Forgot() {
  const [email, setEmail] = useState('');
  const [tenant, setTenant] = useState(localStorage.getItem('tenantSlug') || '');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  // --- same logo behavior as Login.jsx ---
  const [logoBroken, setLogoBroken] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    
    const hint = resolveTenantHint(tenant);
    if (!hint) {
      alert('Unable to determine shop/organisation. Please enter a valid shop name.');
      return;
    }
    
    setSending(true);
    try {
      const body = { email: email.trim(), tenant: hint };
      await api.post('/auth/forgot', body, { headers: { 'X-Tenant-ID': hint } });
      setSent(true); // privacy-preserving
    } catch {
      setSent(true);
    } finally {
      setSending(false);
    }
  }

  const providerHref = useMemo(() => {
    const m = email.split('@')[1]?.toLowerCase() || '';
    if (m.includes('gmail')) return 'https://mail.google.com/';
    if (m.includes('outlook') || m.includes('live') || m.includes('hotmail')) return 'https://outlook.live.com/';
    if (m.includes('yahoo')) return 'https://mail.yahoo.com/';
    return 'mailto:';
  }, [email]);

  // Initialize tenant on mount if not already set
  useEffect(() => {
    if (!tenant) {
      const guess = resolveTenantHint();
      if (guess) setTenant(guess);
    }
  }, []);

  return (
    <div className="forgot-wrap">
      <div className="forgot-card" role="dialog" aria-labelledby="forgotTitle">
        {/* unified company logo (public/brand/logo.png) — identical to Login.jsx */}
        {!logoBroken ? (
          <img
            src="/brand/logo.png"
            alt="Company logo"
            className="brand-logo"
            onError={() => setLogoBroken(true)}
            draggable="false"
          />
        ) : (
          <div className="logo-fallback" aria-hidden="true" />
        )}

        <h2 id="forgotTitle" className="title">Forgot your password?</h2>
        {!sent ? (
          <>
            <p className="muted" style={{ marginTop: 4 }}>
              Enter your email and we’ll send a secure reset link.
            </p>

            <form onSubmit={submit} className="form">
              <label className="field">
                <span className="label">Shop / Organisation Name</span>
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. anvi-electronics"
                  value={tenant}
                  onChange={(e)=>setTenant(e.target.value)}
                  autoComplete="off"
                  required
                  disabled={sending}
                />
              </label>

              <label className="field">
                <span className="label">Email</span>
                <input
                  className="input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  inputMode="email"
                  autoComplete="email"
                  required
                  disabled={sending}
                />
              </label>

              <button className="btn primary" type="submit" disabled={sending}>
                {sending ? (
                  <span className="spinner-wrap">
                    <span className="spinner" aria-hidden />
                    Sending…
                  </span>
                ) : 'Send reset link'}
              </button>

              <div className="footer">
                <a className="link" href="/login">Back to Sign in</a>
              </div>
            </form>
          </>
        ) : (
          <SuccessView email={email} providerHref={providerHref} tenant={tenant} />
        )}
      </div>

      <style>{`
        /* ——— Match Login.jsx container behavior ——— */
        .forgot-wrap {
          min-height: calc(100dvh - 64px);
          display: grid; place-items: center;
          padding: 16px; overflow: hidden;
          background: transparent; /* lets wallpaper show */
          color: var(--text, #0f172a);
        }
        @media (min-width: 640px) { .forgot-wrap { padding: 24px; } }

        .forgot-card {
          width: 100%; max-width: 420px; background: var(--bg-elev, #fff);
          border: 1px solid var(--hairline, #e5e7eb);
          border-radius: 16px; box-shadow: var(--shadow, 0 10px 30px rgba(2,6,23,.08));
          padding: 20px; box-sizing: border-box;
        }

        /* === same brand logo styles as Login.jsx === */
        .brand-logo {
          display: block;
          width: 200px;
          height: 200px;
          object-fit: contain;
          margin: 0 auto 16px;
          border-radius: 28px;
          user-select: none;
          pointer-events: none;
          background: transparent;
          border: none;
          box-shadow: none;
          filter: none;
        }
        .logo-fallback {
          width: 64px; height: 64px; border-radius: 14px; margin: 0 auto 12px;
          background: radial-gradient(80% 80% at 30% 20%, #93c5fd, #60a5fa);
          box-shadow: 0 6px 18px rgba(37,99,235,0.28);
        }

        .title { margin: 0 0 10px; font-size: 20px; line-height: 1.2; text-align: center; }
        .muted { color: var(--muted, #475569); }

        .form { display: grid; gap: 10px; margin-top: 6px; }
        .field { display: flex; flex-direction: column; gap: 6px; }
        .label { font-size: 12px; color: var(--muted, #475569); }

        .input {
          width: 100%; height: 40px; border-radius: 10px; border: 1px solid var(--hairline, #e5e7eb);
          background: var(--bg, #fff); color: var(--text, #0f172a); padding: 0 12px; outline: none;
        }
        .input:focus {
          border-color: var(--accent, #7c3aed);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent,#7c3aed) 26%, transparent);
        }

        .btn.primary {
          margin-top: 6px;
          width: 100%; height: 40px; border-radius: 10px; border: 0;
          background: var(--link, #2563eb); color: #fff; font-weight: 600; cursor: pointer;
        }
        .btn.primary:disabled { opacity: .7; cursor: not-allowed; }

        .spinner-wrap { display: inline-flex; align-items: center; gap: 8px; }
        .spinner {
          width: 16px; height: 16px; border-radius: 999px;
          border: 2px solid color-mix(in srgb, var(--bg-elev,#fff) 60%, var(--text,#0b1220) 40%);
          border-top-color: #fff;
          animation: spin .8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .footer { margin-top: 10px; display: flex; justify-content: flex-end; }
        .link { color: var(--link, #2563eb); text-decoration: none; font-size: 14px; }
        .link:hover { text-decoration: underline; }

        /* keep small-screen safety identical to Login */
        html, body, #root { max-width: 100%; overflow-x: hidden; }
      `}</style>
    </div>
  );
}

function SuccessView({ email, providerHref, tenant }) {
  const [seconds, setSeconds] = useState(30);
  const [resending, setResending] = useState(false);
  const [justResent, setJustResent] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  async function resend() {
    if (resending || seconds > 0) return;
    setResending(true);
    setJustResent(false);
    try {
      const hint = (tenant || '').trim();
      const body = { email: (email || '').trim(), tenant: hint };
      await api.post('/auth/forgot', body, { headers: { 'X-Tenant-ID': hint } });
      setJustResent(true);
      setSeconds(30);
    } catch {
      setJustResent(true);
      setSeconds(30);
    } finally {
      setResending(false);
    }
  }

  return (
    <div>
      <h3 className="title" style={{ marginBottom: 6, textAlign:'center' }}>Check your inbox</h3>
      <p className="muted" style={{ marginTop: 0, textAlign:'center' }}>
        If that email exists, we’ve sent a reset link. The link expires soon for security.
      </p>

      <div className="actions" style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap', marginTop:12 }}>
        <a className="btn primary" href={providerHref} target="_blank" rel="noreferrer" style={{ flex:'1 1 220px' }}>
          Open mail app
        </a>
        <button
          className="btn primary"
          onClick={resend}
          disabled={resending || seconds > 0}
          title={seconds > 0 ? `Wait ${seconds}s` : 'Resend link'}
          style={{ background: 'var(--accent,#7c3aed)', flex:'1 1 220px' }}
        >
          {resending ? 'Sending…' : seconds > 0 ? `Resend in ${seconds}s` : 'Resend link'}
        </button>
      </div>
    </div>
  );
}
